import { useEffect, useRef, useState } from "react";
import { MAX_BROCHURE_BYTES } from "@mizpah/shared";
import { api, uploadToS3, AdminError } from "../adminClient.js";

interface Current { version: number; fileName: string; uploadedAt: string; }
interface Row { courseId: string; name: string; current: Current | null; }
interface Version { version: number; fileName: string; uploadedAt: string; currentMarker?: string; }

export default function BrochuresPage() {
  const [rows, setRows] = useState<Row[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const [versions, setVersions] = useState<Version[]>([]);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const uploadFor = useRef<string>("");

  const load = () => api<{ items: Row[] }>("/admin/brochures").then((d) => setRows(d.items)).catch((e) => setError(msg(e)));
  useEffect(() => { load(); }, []);

  async function toggleHistory(courseId: string) {
    if (open === courseId) return setOpen(null);
    setOpen(courseId);
    try { const d = await api<{ versions: Version[] }>(`/admin/brochures/${courseId}/versions`); setVersions(d.versions); }
    catch (e) { setError(msg(e)); }
  }

  function pickFile(courseId: string) { uploadFor.current = courseId; fileInput.current?.click(); }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const courseId = uploadFor.current;
    if (!/\.pdf$/i.test(file.name)) return setError("Brochure must be a PDF.");
    if (file.size > MAX_BROCHURE_BYTES) return setError("File too large (max 10 MB).");
    setBusy(courseId); setError("");
    try {
      const { uploadUrl, s3Key } = await api<{ uploadUrl: string; s3Key: string }>(
        `/admin/brochures/${courseId}/upload-url`, { method: "POST", body: { fileName: file.name, sizeBytes: file.size } }
      );
      await uploadToS3(uploadUrl, file);
      const { version } = await api<{ version: number }>(
        `/admin/brochures/${courseId}/versions`, { method: "POST", body: { s3Key, fileName: file.name, sizeBytes: file.size } }
      );
      // New monthly brochure becomes the current one.
      await api(`/admin/brochures/${courseId}/current`, { method: "PUT", body: { version } });
      await load();
      if (open === courseId) toggleHistory(courseId), toggleHistory(courseId);
    } catch (err) { setError(msg(err)); }
    finally { setBusy(""); }
  }

  async function setCurrent(courseId: string, version: number) {
    setBusy(courseId); setError("");
    try { await api(`/admin/brochures/${courseId}/current`, { method: "PUT", body: { version } }); await load(); const d = await api<{ versions: Version[] }>(`/admin/brochures/${courseId}/versions`); setVersions(d.versions); }
    catch (e) { setError(msg(e)); } finally { setBusy(""); }
  }

  return (
    <>
      <h1>Brochures</h1>
      <p className="subhead">Upload a new PDF for any program and it becomes the current one. The auto-email always links the current brochure; past versions are kept.</p>
      {error && <div className="login-err" style={{ marginBottom: 12 }}>{error}</div>}
      <input ref={fileInput} type="file" accept="application/pdf" hidden onChange={onFile} />

      {rows.map((r) => (
        <div key={r.courseId} className="card pad" style={{ marginBottom: 12 }}>
          <div className="row">
            <div>
              <div className="nm">{r.name}</div>
              <div className="sub">{r.current ? `${r.current.fileName} · v${r.current.version} · ${new Date(r.current.uploadedAt).toLocaleDateString()}` : "No brochure yet"}</div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="rbtn" onClick={() => toggleHistory(r.courseId)}>{open === r.courseId ? "Hide history" : "History"}</button>
              <button className="rbtn coral" disabled={busy === r.courseId} onClick={() => pickFile(r.courseId)}>{busy === r.courseId ? "Uploading..." : "Upload new"}</button>
            </div>
          </div>
          {open === r.courseId && (
            <ul className="tl" style={{ marginTop: 14 }}>
              {versions.length === 0 && <li>No versions yet.</li>}
              {versions.map((v) => (
                <li key={v.version}>
                  <div className="row">
                    <span><strong>v{v.version}</strong> {v.fileName} <span className="t">· {new Date(v.uploadedAt).toLocaleDateString()}</span></span>
                    {v.currentMarker ? <span className="badge b-Enrolled">Current</span> : <button className="rbtn" disabled={!!busy} onClick={() => setCurrent(r.courseId, v.version)}>Set current</button>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </>
  );
}

const msg = (e: unknown) => (e instanceof AdminError ? e.message : "Something went wrong");
